<template>
    <div class="learning-progress" data-test-view="learning-progress">
        <l-headline
            :title="$t(`learning.progress.title`)"
            :sub-title="$t(`learning.progress.subTitle`)"
        ></l-headline>

        <div v-if="todo.length" class="learning-progress-list">
            <h4 class="color-dark">
                <strong>{{ $t(`learning.progress.todo.title`) }}</strong>
            </h4>

            <hr />

            <l-paginate
                :list="todo"
                :items-per-page="5"
                :on-item-click="goToStepDetails"
                :no-data-show="false"
                :pagination-top="true"
                :enable-scroll-to-top="false"
                :link-to-route="false"
            >
                <template #item="{ item }">
                    <l-list-item
                        :id="item.progressId"
                        class="clickable element-list-item"
                        data-test-class="progress-todo-list-item"
                    >
                        <template #title>
                            {{ item.learningElement.name }}
                        </template>
                        <template #subtitle>
                            <div class="element-subtitle ellipsis">
                                <i class="fal fa-clock"></i>{{ $t(`learning.progress.todo.paths`) }}
                                <span data-test-id="progress-todo-path-name">{{
                                    item.learningPath.name
                                }}</span>
                                <span v-if="item.deadlineDays < 0">
                                    ({{ $t(`learning.progress.todo.deadline.overdue`) }})
                                </span>
                                <span v-else>
                                    ({{
                                        $tc(
                                            `learning.progress.todo.deadline.days`,
                                            item.deadlineDays,
                                            {
                                                count: item.deadlineDays,
                                            },
                                        )
                                    }})
                                </span>
                            </div>
                            <div
                                class="element-show-path fake-link"
                                data-test-id="progress-todo-toggle-timeline"
                                tabindex="0"
                                @click.stop="goToProgressDetails(item.progressId)"
                                @keyup.enter.stop="goToProgressDetails(item.progressId)"
                            >
                                <span v-if="showFullPath === item.progressId">
                                    <i class="fa fa-caret-down"></i
                                    >{{ $t(`learning.progress.todo.fullPath.hide`) }}
                                </span>
                                <span v-else>
                                    <i class="fa fa-caret-right"></i
                                    >{{ $t(`learning.progress.todo.fullPath.show`) }}
                                </span>
                            </div>
                        </template>
                        <template #center>
                            <l-progress :progress-percent="Math.round(item.percentage)" />
                        </template>
                        <template #left>
                            <div class="element-list-item-icon">
                                <img
                                    class="element-list-item-icon-img"
                                    :src="icons[item.learningElement.type]"
                                    :alt="item.learningElement.type"
                                />
                            </div>
                        </template>

                        <template #expand>
                            <div
                                v-show="showFullPath === item.progressId"
                                class="full-path"
                                data-test-id="progress-todo-timeline"
                                @click.stop="() => {}"
                                @keyup.enter.stop="() => {}"
                            >
                                <hr />
                                <l-learning-progress-timeline
                                    :progress="
                                        list.find(progress => progress._id === item.progressId)
                                    "
                                    :on-step-click-handler="goToStepDetails"
                                />
                            </div>
                        </template>
                    </l-list-item>
                </template>
            </l-paginate>
        </div>
        <div v-else>
            <l-no-data :text="$t(`learning.progress.empty`)" />
        </div>

        <br />

        <div v-show="completed.length" class="learning-progress-list list-completed">
            <h4 class="color-dark">
                <strong>{{ $t(`learning.progress.completed.title`) }}</strong>
            </h4>

            <hr />

            <l-paginate
                :list="completed"
                :items-per-page="5"
                :on-item-click="progress => goToProgressDetails(progress._id)"
                :no-data-show="false"
                :pagination-top="true"
                :enable-scroll-to-top="false"
                :link-to-route="false"
            >
                <template #item="{ item }">
                    <l-list-item
                        :id="item._id"
                        class="clickable"
                        data-test-class="progress-completed-list-item"
                    >
                        <template #title>
                            <span data-test-id="progress-completed-path-name">{{
                                item.learningPath.name
                            }}</span>
                        </template>
                        <template #subtitle>
                            <div class="element-subtitle ellipsis">
                                <i class="fal fa-clock"></i
                                >{{ $t(`learning.progress.completed.date`) }}
                                {{ item.completed | moment("MMM DD, YYYY") }}
                            </div>
                            <div
                                class="element-show-path fake-link"
                                tabindex="0"
                                @click.stop="goToProgressDetails(item._id)"
                                @keyup.enter.stop="goToProgressDetails(item._id)"
                            >
                                <span v-if="showFullPath === item._id">
                                    <i class="fa fa-caret-down"></i
                                    >{{ $t(`learning.progress.todo.fullPath.hide`) }}
                                </span>
                                <span v-else>
                                    <i class="fa fa-caret-right"></i
                                    >{{ $t(`learning.progress.todo.fullPath.show`) }}
                                </span>
                            </div>
                        </template>

                        <template #left>
                            <button
                                v-if="item.lastCertificateId"
                                class="btn btn-primary btn-rounded-primary"
                                data-test-id="learning-path-view-certificate"
                                @click.stop="goToCertificate(item.lastCertificateId)"
                            >
                                {{ $t(`learning.progress.completed.viewCertificate`) }}
                            </button>
                            <button
                                v-else
                                class="btn btn-primary btn-rounded-primary"
                                data-test-id="learning-path-issue-certificate"
                                @click.stop="issueCertificate(item._id)"
                            >
                                {{ $t(`learning.progress.completed.issueCertificate`) }}
                            </button>
                        </template>

                        <template #expand>
                            <div
                                v-show="showFullPath === item._id"
                                class="full-path"
                                @click.stop="() => {}"
                                @keyup.enter.stop="() => {}"
                            >
                                <hr />
                                <l-learning-progress-timeline
                                    :progress="item"
                                    :on-step-click-handler="goToStepDetails"
                                />
                            </div>
                        </template>
                    </l-list-item>
                </template>
            </l-paginate>
        </div>

        <l-modal v-show="showModal" size="l" :close-on-backdrop-click="true" @close="closeModal">
            <template #header>{{ modalTitle }}</template>
            <template #body>
                <router-view></router-view>
            </template>
        </l-modal>
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import LHeadline from "@/components/LHeadline";
import LListItem from "@/components/LListItem";
import LModal from "@/components/LModal";
import LNoData from "@/components/LNoData";
import LPaginate from "@/components/LPaginate";
import LProgress from "@/components/LProgress";

import LLearningProgressTimeline from "@/components/LLearningProgressTimeline";

import article from "@/img/icons/badge_book.png";
import quiz from "@/img/icons/badge_planchette.png";
import task from "@/img/icons/badge_puzzle.png";
import video from "@/img/icons/badge_projector.png";

export default {
    components: {
        LHeadline,
        LListItem,
        LModal,
        LNoData,
        LPaginate,
        LProgress,

        LLearningProgressTimeline,
    },
    data() {
        return {
            showModal: false,
            modalTitle: "",
            // As we only show one full path on the screen at a given time, we can store the id of the
            // progress we are currently showing in this variable.
            //
            showFullPath: "",
        };
    },
    computed: {
        ...mapGetters({
            list: "learning/progress/list",
            todo: "learning/progress/todo",
            certificate: "learning/certificate/certificate",
        }),
        completed() {
            return this.list
                .filter(progress => progress.completed)
                .sort((a, b) => {
                    if (new Date(a.completed) < new Date(b.completed)) {
                        return 1;
                    }
                    if (new Date(a.completed) > new Date(b.completed)) {
                        return -1;
                    }
                    return 0;
                });
        },
    },
    watch: {
        $route: {
            immediate: true,
            handler(newRoute) {
                this.showModal = newRoute.meta && newRoute.meta.modal;
                this.modalTitle = this.getModalTitleFromRoute(newRoute);
            },
        },
    },
    created() {
        this.icons = {
            article,
            quiz,
            task,
            video,
        };
    },
    methods: {
        goToStepDetails(step) {
            const progressId = step.progressId;
            const stepId = step._id;

            if (step.unlocked) {
                this.$router.push(`/learning/progress/${progressId}/${stepId}`);
            }
        },
        goToProgressDetails(id) {
            if (this.showFullPath === id) {
                this.showFullPath = "";
            } else {
                this.showFullPath = id;
            }

            // Smooth scroll into view
            if (this.showFullPath) {
                this.$nextTick(() => {
                    document
                        .getElementById(id)
                        ?.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });
                });
            }
        },
        goToCertificate(certificateId) {
            this.$router.push(`/learning/certificate/${certificateId}`);
        },
        closeModal() {
            this.$confetti.stop();
            this.$router.push("/learning/progress");
        },
        getModalTitleFromRoute(route) {
            switch (route.name) {
                case "LearningProgressStepDetails": {
                    const step = this.list
                        .find(progress => progress._id === route.params.progressId)
                        ?.progressSteps?.find?.(step => step?._id === route.params.stepId);

                    return this.$t("learning.progress.element.title", {
                        name: step.learningElement.name,
                    });
                }
                case "LearningLibraryElementDetails": {
                    const element = this.list
                        .filter(progress => progress.completed)
                        .map(progress => progress.progressSteps)
                        .flat()
                        .find(step => step?.learningElementCommonId === route.params.elementId)
                        ?.learningElement;

                    return this.$t("learning.library.archive.element.title", {
                        name: element.name,
                    });
                }
                case "LearningProgressCompletion": {
                    return this.$t("learning.progress.completion.completed.title");
                }
                default:
                    return this.$t(`learning.admin.element.details.title.default`);
            }
        },
        async issueCertificate(progressId) {
            await this.$store.dispatch("learning/certificate/issueCertificate", {
                payload: { progressId: progressId },
            });
            this.goToCertificate(this.certificate.certificateId);
        },
    },
};
</script>

<style scoped>
.learning-progress-list >>> .list-item {
    align-items: stretch;
}
.learning-progress-list >>> .list-item-right,
.learning-progress-list >>> .list-item-left {
    width: 27vw;
}
.learning-progress-list >>> .list-item-left {
    display: flex;
    justify-content: flex-end;
}
.learning-progress-list >>> .list-item-center {
    flex-grow: 1;
    text-align: center;
    padding: 0 16px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
}
.learning-progress-list .element-list-item-icon {
    width: 80px;
    text-align: center;
}

.learning-progress-list .element-subtitle span,
.learning-progress-list .element-show-path span {
    margin-right: 8px;
}
.learning-progress-list .element-subtitle i {
    margin-right: 10px;
}

.learning-progress-list .element-show-path i {
    margin-left: 2px;
    margin-right: 16px;
}

.learning-progress-list .element-list-item-icon-img {
    height: 80px;
    opacity: 0.8;
    transition: all 0.3s ease-in;
}
.learning-progress-list .element-list-item:hover .element-list-item-icon-img {
    transform: scale(1.05);
    opacity: 1;
}

/* Completed */
.learning-progress-list.list-completed >>> .list-item-right {
    width: 100%;
}
.learning-progress-list.list-completed >>> .list-item-center,
.learning-progress-list.list-completed >>> .list-item-left {
    width: 0;
    padding: 0;
}
</style>
